import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IcClipboardList, IcPhone, IcPackage, IcCalendar, IcCheck, IcClose, IcTrash, IcBanknote, IcFlower, IcSparkles, IcUser, IcPackageOpen, IcList, IcFilters } from '../../theme/icons';
import Swal from '../../theme/swal';
import { gated, GATE_CANCELLED } from '../../services/identityGate';
import reservationService from '../../services/reservationService';
import serviceManagementService from '../../services/serviceManagementService';
import customerService from '../../services/customerService';
import VenteTabs from '../../components/VenteTabs';
import ReservationProductActions from '../../components/ReservationProductActions';
import SoinsCalendarView from './SoinsCalendarView';
import KI from '../../theme/palette';
import { CardSkeleton } from '../../components/Skeleton';

// Étiquettes Ki Para (Ki_Para_Etiquettes.html) : en attente = contour sky,
// arrivée = contour vert, bloquée = encre, vendue = vert plein, annulée = gris.
const STATUS_META = {
  pending: { label: 'En attente', cls: 'bdg-wait' },
  arrived: { label: 'Arrivée', cls: 'bdg-arrive' },
  blocked: { label: 'Bloquée', cls: 'bdg-ink' },
  delivered: { label: 'Vendue', cls: 'bdg-done' },
  cancelled: { label: 'Annulée', cls: 'bdg-cancel' },
};

// Reserved soins are Sessions (rendez-vous) — shown here too, per owner request.
// Étiquettes : programmé = teinte verte, en cours = gris + texte vert,
// terminé = vert plein, annulé = gris neutre.
const SESSION_STATUS_META = {
  scheduled: { label: 'Programmé', cls: 'bdg-pos' },
  in_progress: { label: 'En cours', cls: 'bdg-live' },
  completed: { label: 'Terminé', cls: 'bdg-done' },
  cancelled: { label: 'Annulé', cls: 'bdg-neutral' },
};

const TABS = [
  { key: '', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'arrived', label: 'Arrivée' },
  { key: 'blocked', label: 'Bloquée' },
  { key: 'delivered', label: 'Vendue' },
  { key: 'cancelled', label: 'Annulée' },
];

// Soins réservés (sessions) filter cases.
const SOIN_TABS = [
  { key: '', label: 'Tous' },
  { key: 'scheduled', label: 'Programmé' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'completed', label: 'Terminé' },
  { key: 'cancelled', label: 'Annulé' },
];

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return d;
  }
};

const ReservationsList = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('soins'); // 'products' | 'soins' — soins réservés par défaut
  const [reservations, setReservations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [soinView, setSoinView] = useState('calendar'); // 'calendar' | 'list'
  // Status buttons collapsed behind a « Filtres » button, same design as seller-sales (owner Studio 2026-08-16)
  const [showFilters, setShowFilters] = useState(false);
  // Catalogue des soins actifs — alimente le formulaire "Nouvelle réservation"
  // du panneau jour (calendrier). Chargé une fois en mode soins.
  const [soinServices, setSoinServices] = useState([]);

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const params = activeTab ? { status: activeTab } : {};
      const data = await reservationService.getReservations(params);
      setReservations(Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []));
      setError(null);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Impossible de charger les réservations.');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await serviceManagementService.getAllSessions();
      const list = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      // newest rendez-vous first
      list.sort((a, b) => new Date(b.scheduled_time) - new Date(a.scheduled_time));
      setSessions(list);
      setError(null);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Impossible de charger les soins réservés.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'soins') fetchSessions();
    else fetchReservations();
  }, [mode, fetchReservations, fetchSessions]);

  useEffect(() => {
    if (mode !== 'soins' || soinServices.length > 0) return;
    (async () => {
      try {
        const data = await serviceManagementService.getAllServices();
        const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        setSoinServices(list.filter((s) => s.is_active && s.type === 'SOIN'));
      } catch (err) {
        console.error('Error fetching soin services:', err);
      }
    })();
  }, [mode, soinServices.length]);

  const searchSoinCustomers = async (q) => {
    const data = await customerService.searchCustomers(q);
    return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  };

  // Création du rendez-vous depuis le panneau jour du calendrier. Retourne true
  // si créé (le formulaire se referme), false sinon (les champs restent remplis).
  const handleCreateSession = async (payload) => {
    try {
      if ((await gated('Qui réserve le soin ?', () => serviceManagementService.createSession(payload))) === GATE_CANCELLED) return;
      Swal.fire({ icon: 'success', title: 'Soin réservé', text: 'Le rendez-vous a été créé.', timer: 1600, showConfirmButton: false });
      fetchSessions();
      return true;
    } catch (err) {
      console.error('Error creating soin reservation:', err);
      // Surface the REAL backend reason instead of a generic message.
      const data = err?.response?.data;
      let msg = data?.error || data?.detail;
      if (!msg && data && typeof data === 'object') {
        const first = Object.values(data)[0];
        msg = Array.isArray(first) ? first[0] : (typeof first === 'string' ? first : null);
      }
      if (!msg && err?.response?.status) msg = `Erreur ${err.response.status}`;
      Swal.fire('Erreur', msg || 'Erreur lors de la réservation du soin.', 'error');
      return false;
    }
  };

  const changeSessionStatus = async (session, status) => {
    try {
      await serviceManagementService.updateSessionStatus(session.id, status);
      fetchSessions();
    } catch (err) {
      console.error('Error updating session:', err);
      Swal.fire('Erreur', 'Impossible de mettre à jour le soin.', 'error');
    }
  };

  // « Terminer » = même flux que « Sessions du jour » sur le dashboard : ouvre le
  // panier avec le soin au prix réservé et le client sélectionné. Une session NON
  // payée ne passe « completed » qu'après « Encaisser la vente » (sessionId dans
  // le deep-link) ; une session déjà payée à la réservation est complétée tout de
  // suite (rien à encaisser) et n'est pas refacturée.
  const handleFinishSoin = async (s) => {
    if (s.payment_status === 'paid') {
      try { await serviceManagementService.updateSessionStatus(s.id, 'completed'); } catch (err) { console.error('Finish session status failed:', err); }
    }
    navigate('/seller-sales', {
      state: {
        addServiceId: s.service,
        serviceName: s.service_name,
        servicePrice: s.service_price,
        serviceRemise: s.service_remise,
        soinPaid: s.payment_status === 'paid',
        clientId: s.customer,
        sessionId: s.id,
        openPanier: true,
      },
    });
  };

  const handleDeleteSession = async (session) => {
    const res = await Swal.fire({
      title: 'Supprimer le rendez-vous ?',
      text: `${session.customer_name} — ${session.service_name}`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try {
      await serviceManagementService.deleteSession(session.id);
      fetchSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      Swal.fire('Erreur', 'Impossible de supprimer le soin.', 'error');
    }
  };

  const handleDelete = async (reservation) => {
    const res = await Swal.fire({
      title: 'Supprimer la réservation ?',
      text: `${reservation.customer_name} — ${reservation.product_name}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try {
      if ((await gated('Qui supprime la réservation ?', () => reservationService.deleteReservation(reservation.id))) === GATE_CANCELLED) return;
      fetchReservations();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      Swal.fire('Erreur', 'Impossible de supprimer la réservation.', 'error');
    }
  };

  // Soins are fetched in full, then filtered client-side by the selected case.
  const shownSessions = activeTab ? sessions.filter((s) => s.status === activeTab) : sessions;

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Bascule de référence du kit « pilule » (.segp, index.css) : les bascules
          de « Nouvelle vente » et « Historique » ont été alignées dessus
          (owner 2026-09-06). L'icône du soin est celle des services (IcSparkles). */}
      <VenteTabs>
        <div className="segp">
          {/* Order Soin → Produits per owner Studio 2026-08-13 (soins stay the default) */}
          <button
            type="button"
            onClick={() => { setMode('soins'); setActiveTab(''); }}
            className={mode === 'soins' ? 'segp-on' : 'segp-off'}
          >
            <IcSparkles size={13} /> Soin
          </button>
          <button
            type="button"
            onClick={() => { setMode('products'); setActiveTab(''); }}
            className={mode === 'products' ? 'segp-on' : 'segp-off'}
          >
            <IcPackageOpen size={13} /> Produits
          </button>
        </div>
      </VenteTabs>

      {/* Status filters (produits) derrière un bouton « Filtres » (même design que
          seller-sales) + vue Calendrier/Liste pour les soins */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {mode === 'products' && (
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="btn-s relative ml-auto h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
          >
            <IcFilters className="text-sm" /> Filtres
          </button>
        )}
        {mode === 'soins' && (
          /* Single toggle button — styled like the « Filtres » button on the
             seller-sales page, without border (owner request). Click switches
             view; shows the view it will switch TO. */
          <button
            type="button"
            onClick={() => setSoinView((v) => (v === 'calendar' ? 'list' : 'calendar'))}
            className="btn-s relative ml-auto h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
          >
            {soinView === 'calendar' ? (
              <><IcList className="text-sm" /> Liste</>
            ) : (
              <><IcCalendar className="text-sm" /> Calendrier</>
            )}
          </button>
        )}
      </div>

      {/* Filter panel (produits): les boutons de statut, style panneau seller-sales */}
      {mode === 'products' && showFilters && (
        <div className="flt-card rounded-2xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">{error}</div>
      )}

      {loading && reservations.length === 0 && sessions.length === 0 ? (
        <div className="text-center py-16">
          <CardSkeleton />
        </div>
      ) : mode === 'soins' ? (
        soinView === 'calendar' ? (
          <SoinsCalendarView
            sessions={shownSessions}
            statusMeta={SESSION_STATUS_META}
            soinServices={soinServices}
            onSearchCustomers={searchSoinCustomers}
            onCreate={handleCreateSession}
            onComplete={handleFinishSoin}
            onCancel={(s) => changeSessionStatus(s, 'cancelled')}
            onDelete={handleDeleteSession}
          />
        ) : shownSessions.length === 0 ? (
          <div className="kp-fill justify-center">
            <div className="empty-ki py-14">
              <IcFlower size={28} className="empty-ki-icon w-[28px] h-[28px] mb-2" />
              <p className="empty-ki-title text-sm mt-0">Aucun soin réservé{activeTab ? ' dans ce statut' : ''}.</p>
            </div>
          </div>
        ) : (
          <div className="kp-fill">
          <div className="kp-scroll space-y-3 pr-1">
            {shownSessions.map((s) => {
              const meta = SESSION_STATUS_META[s.status] || SESSION_STATUS_META.scheduled;
              return (
                <div key={s.id} className="bg-white border border-gray-200 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-lg bg-white border-[1.5px] border-gray-200 flex items-center justify-center flex-shrink-0 text-gray-400"><IcSparkles /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-bold text-gray-900 truncate">{s.service_name}</p>
                        <span className={`bdg flex-shrink-0 ${meta.cls}`}>{meta.label}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-0.5 inline-flex items-center gap-1"><IcUser size={10} className="text-gray-400" /> {s.customer_name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1"><IcCalendar size={10} /> {s.scheduled_time}</span>
                        {Number(s.final_price) > 0 && <span className="inline-flex items-center gap-1"><IcBanknote size={10} /> {Number(s.final_price).toFixed(3)} DT</span>}
                      </div>
                      {s.notes && <p className="text-xs text-gray-500 mt-1.5 italic">“{s.notes}”</p>}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                    {/* Même grammaire que la carte du calendrier (SoinsCalendarView) :
                        Supprimer = Destructif bordé (.btn-db) isolé à gauche via mr-auto ·
                        Annuler = .btn-g · Terminer = Primaire vert (.btn-p) au bord droit. */}
                    <button onClick={() => handleDeleteSession(s)} className="btn-db px-3 py-1.5 text-xs gap-1.5 mr-auto"><IcTrash size={11} /> Supprimer</button>
                    {(s.status === 'scheduled' || s.status === 'in_progress') && (
                      <button onClick={() => changeSessionStatus(s, 'cancelled')} className="btn-g px-3 py-1.5 text-xs gap-1.5"><IcClose size={11} /> Annuler</button>
                    )}
                    {s.status === 'scheduled' && (
                      <button onClick={() => handleFinishSoin(s)} className="btn-p px-3 py-1.5 text-xs gap-1.5"><IcCheck size={11} /> Terminer</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        )
      ) : reservations.length === 0 ? (
        <div className="kp-fill justify-center">
          <div className="empty-ki py-14">
            <IcClipboardList size={28} className="empty-ki-icon w-[28px] h-[28px] mb-2" />
            <p className="empty-ki-title text-sm mt-0">Aucune réservation{activeTab ? ' dans ce statut' : ''}.</p>
          </div>
        </div>
      ) : (
        <div className="kp-fill">
        <div className="kp-scroll space-y-3 pr-1">
          {reservations.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const arrival = formatDate(r.expected_arrival_date);
            return (
              <div key={r.id} className="bg-white border border-gray-200 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {r.product_image ? (
                      <img src={r.product_image} alt={r.product_name} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <IcPackage className="text-gray-300" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold text-gray-900 truncate">{r.product_name}</p>
                      <span className={`bdg flex-shrink-0 ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {r.customer_name}
                      {r.customer_phone && (
                        <span className="text-gray-400 ml-2 inline-flex items-center gap-1"><IcPhone size={10} /> {r.customer_phone}</span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span>Quantité : <span className="font-semibold text-gray-700">{r.quantity}</span></span>
                      {arrival && <span className="inline-flex items-center gap-1"><IcCalendar size={10} /> Arrivée : {arrival}</span>}
                      {Number(r.deposit_amount) > 0 && (
                        <span className="inline-flex items-center gap-1"><IcBanknote size={10} /> <b className="tabular-nums text-gray-900">Avance {Number(r.deposit_amount).toFixed(3)} DT</b></span>
                      )}
                    </div>
                    {r.note && <p className="text-xs text-gray-500 mt-1.5 italic">“{r.note}”</p>}
                  </div>
                </div>

                {/* Actions — même composant partagé que la carte « Réservation de
                    produits » du dashboard, pour un comportement STRICTEMENT
                    identique (Marquer arrivée grisé tant que le stock n'est pas
                    entré, Bloquer/Débloquer, Vendre au client, Annuler confirmé). */}
                <div className="flex flex-wrap items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
                  {/* Supprimer passé À GAUCHE (owner 2026-09-07) — même grammaire
                      que les cartes de session plus haut : destructif bordé
                      (.btn-db) isolé à gauche via mr-auto, les autres actions
                      restent groupées à droite. */}
                  <button onClick={() => handleDelete(r)} className="btn-db px-3 py-1.5 text-xs gap-1.5 mr-auto">
                    <IcTrash size={11} /> Supprimer
                  </button>
                  <ReservationProductActions reservation={r} onChanged={fetchReservations} />
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}
    </div>
  );
};

export default ReservationsList;
